import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from '../lib/apiConfig';
import { apiFetch } from '../lib/apiFetch';

export type PermissionCode = string;

interface Permission {
  id: number;
  codigo: PermissionCode;
  modulo: string;
  nome: string;
  descricao: string;
  categoria: string;
}

interface Role {
  id: number;
  nome: string;
  descricao: string;
  is_system: boolean;
}

interface UserPermissions {
  userId: number;
  roles: Role[];
  permissions: Permission[];
  permissionCodes: Set<PermissionCode>;
}

interface PermissionsContextType {
  permissions: UserPermissions | null;
  loading: boolean;
  hasPermission: (code: PermissionCode) => boolean;
  hasAnyPermission: (codes: PermissionCode[]) => boolean;
  hasAllPermissions: (codes: PermissionCode[]) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  refreshPermissions: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { userData, session, loading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  // loading = true as long as auth is loading OR permissions haven't been fetched yet
  const loading = authLoading || !permissionsLoaded;

  const fetchPermissions = async () => {
    if (!userData?.id || !session?.access_token) {
      setPermissions(null);
      setPermissionsLoaded(true);
      return;
    }

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/v1/usuarios/${userData.id}/permissions`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success && result.data) {
          const permissionCodes = new Set(
            result.data.permissions.map((p: Permission) => p.codigo)
          );
          
          setPermissions({
            ...result.data,
            permissionCodes
          });
        } else {
          setPermissions(null);
        }
      } else {
        setPermissions(null);
      }
    } catch (error) {
      console.error('❌ Error fetching permissions:', error);
      setPermissions(null);
    } finally {
      setPermissionsLoaded(true);
    }
  };

  useEffect(() => {
    // Wait until auth loading is complete
    if (authLoading) return;

    // If session exists but userData hasn't loaded yet, wait
    // (authLoading goes false before fetchUserData completes in AuthContext)
    if (session && !userData) return;

    setPermissionsLoaded(false);
    fetchPermissions();
  }, [userData?.id, session?.access_token, authLoading]);

  // Re-fetch permissions when the tab regains focus (handles role changes made in another tab
  // or by an admin while the user is logged in)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userData?.id && session?.access_token) {
        fetchPermissions();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userData?.id, session?.access_token]);

  const hasPermission = (code: PermissionCode): boolean => {
    if (!permissions) return false;
    return permissions.permissionCodes.has(code);
  };

  const hasAnyPermission = (codes: PermissionCode[]): boolean => {
    if (!permissions) return false;
    return codes.some(code => permissions.permissionCodes.has(code));
  };

  const hasAllPermissions = (codes: PermissionCode[]): boolean => {
    if (!permissions) return false;
    return codes.every(code => permissions.permissionCodes.has(code));
  };

  const isAdmin = hasAnyPermission(['users.delete', 'roles.manage', 'groups.create']);
  const isSuperAdmin = hasPermission('groups.view.all');

  const value: PermissionsContextType = {
    permissions,
    loading,  // derived: authLoading || !permissionsLoaded
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAdmin,
    isSuperAdmin,
    refreshPermissions: fetchPermissions
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};
